import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiSave, FiCopy, FiCheckCircle, FiAlertCircle } from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "../components/ToastProvider";
import "../styles/Dashboard.css";
import "../styles/Modal.css";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.sqir.link";
const SCRIPT_URL = `${API_BASE.replace('/api', '')}/lsq.js`;

const CONVERSION_GOALS = [
  { value: "signup", label: "Signup", description: "Track user registrations" },
  { value: "purchase", label: "Purchase", description: "Track completed purchases" },
  { value: "lead", label: "Lead", description: "Track lead form submissions" },
  { value: "custom", label: "Custom", description: "Track a custom conversion event" },
];

export default function AdvancedConversionsEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [link, setLink] = useState(null);
  const [conversionGoal, setConversionGoal] = useState("signup");
  const [copiedScript, setCopiedScript] = useState(false);
  const [copiedPixel, setCopiedPixel] = useState(false);
  
  // Analytics data
  const [conversionData, setConversionData] = useState(null);
  
  const fetchLink = useCallback(async () => {
    try {
      const data = await api.getLink(id);
      setLink(data);
      if (data.conversion_goal) {
        setConversionGoal(data.conversion_goal);
      }
      
      // Try to fetch conversion analytics
      try {
        const analytics = await api.getLinkConversions(id);
        setConversionData(analytics);
      } catch (e) {
        // May not have any conversions yet
      }
    } catch (err) {
      addToast("Failed to load link", "error");
      navigate("/advanced/conversions");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, addToast]);

  useEffect(() => {
    document.title = "Configure Conversions - LinkSquire";
    fetchLink();
  }, [fetchLink]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updateLink(id, {
        conversions_enabled: true,
        conversion_goal: conversionGoal
      });
      addToast("Conversion settings saved", "success");
      navigate("/advanced/conversions");
    } catch (err) {
      addToast(err.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async () => {
    if (!window.confirm("Are you sure you want to disable conversion tracking for this link?")) return;
    
    setSaving(true);
    try {
      await api.updateLink(id, { conversions_enabled: false });
      addToast("Conversion tracking disabled", "success");
      navigate("/advanced/conversions");
    } catch (err) {
      addToast(err.message || "Failed to disable", "error");
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'script') {
        setCopiedScript(true);
        setTimeout(() => setCopiedScript(false), 2000);
      } else {
        setCopiedPixel(true);
        setTimeout(() => setCopiedPixel(false), 2000);
      }
      addToast("Copied to clipboard", "success");
    } catch (err) {
      addToast("Failed to copy", "error");
    }
  };

  const scriptSnippet = `<script src="${SCRIPT_URL}" async></script>
<script>
  window.lsq = window.lsq || [];
  lsq.push(['track', '${conversionGoal}']);
</script>`;

  const pixelSnippet = `<img src="${API_BASE}/track/pixel?goal=${conversionGoal}" width="1" height="1" style="display:none" />`;

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button className="btn-sm" onClick={() => navigate("/advanced/conversions")} style={{ padding: '6px', background: 'transparent', border: '1px solid var(--border)' }}>
            <FiArrowLeft />
          </button>
          <h1 style={{ fontSize: '20px' }}>Configure Conversion Tracking</h1>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn-sm btn-sm-danger" onClick={handleDisable} disabled={saving}>
            {saving ? <span className="spinner-sm dark" /> : null} Disable Tracking
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? <><span className="spinner-sm" /> Saving...</> : <><FiSave /> Save Configuration</>}
          </button>
        </div>
      </div>
      
      <div style={{ marginBottom: '24px', padding: '16px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', fontWeight: 600 }}>Tracking Link</div>
        <div style={{ fontSize: '15px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '8px' }}>
          {link.title || link.short_code} 
          <a href={link.short_url} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 400, textDecoration: 'none' }}>{link.short_url}</a>
        </div>
      </div>

      {/* Conversion Stats */}
      {conversionData && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div style={{ padding: '20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Total Conversions</div>
            <div style={{ fontSize: '28px', fontWeight: 600 }}>{conversionData.total_conversions || 0}</div>
          </div>
          <div style={{ padding: '20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Conversion Rate</div>
            <div style={{ fontSize: '28px', fontWeight: 600 }}>{conversionData.conversion_rate || 0}%</div>
          </div>
          <div style={{ padding: '20px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Total Value</div>
            <div style={{ fontSize: '28px', fontWeight: 600 }}>${(conversionData.total_value || 0).toFixed(2)}</div>
          </div>
        </div>
      )}

      <div className="links-section" style={{ padding: '24px', marginBottom: '24px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '16px', marginBottom: '4px' }}>Conversion Goal</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Select the type of action you want to track.</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', maxWidth: '700px' }}>
          {CONVERSION_GOALS.map(goal => (
            <button
              key={goal.value}
              onClick={() => setConversionGoal(goal.value)}
              style={{
                padding: '16px',
                border: conversionGoal === goal.value ? '2px solid var(--primary)' : '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                background: conversionGoal === goal.value ? 'rgba(15, 98, 254, 0.05)' : 'var(--surface)',
                cursor: 'pointer',
                textAlign: 'center',
                transition: 'all 0.15s ease'
              }}
            >
              <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '4px' }}>{goal.label}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{goal.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="links-section" style={{ padding: '24px' }}>
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '16px', marginBottom: '4px' }}>Installation</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Add one of these snippets to your thank-you or confirmation page.</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '16px', background: '#fef3c7', color: '#92400e', borderRadius: '8px', marginBottom: '24px', fontSize: '14px', lineHeight: 1.5 }}>
          <FiAlertCircle style={{ marginTop: '2px', flexShrink: 0 }} />
          <div>
            <strong>How it works:</strong> When a user clicks your link, we set a tracking cookie. When they reach your thank-you page with this script, we record the conversion and attribute it to the original click.
          </div>
        </div>

        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ fontWeight: 500, fontSize: '14px' }}>JavaScript Snippet (Recommended)</label>
            <button 
              onClick={() => copyToClipboard(scriptSnippet, 'script')}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
            >
              {copiedScript ? <><FiCheckCircle /> Copied</> : <><FiCopy /> Copy</>}
            </button>
          </div>
          <pre style={{ 
            background: '#1e1e1e', 
            color: '#d4d4d4', 
            padding: '16px', 
            borderRadius: '8px', 
            overflow: 'auto',
            fontSize: '13px',
            lineHeight: 1.5
          }}>
            <code>{scriptSnippet}</code>
          </pre>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ fontWeight: 500, fontSize: '14px' }}>Tracking Pixel (No JavaScript)</label>
            <button 
              onClick={() => copyToClipboard(pixelSnippet, 'pixel')}
              style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
            >
              {copiedPixel ? <><FiCheckCircle /> Copied</> : <><FiCopy /> Copy</>}
            </button>
          </div>
          <pre style={{ 
            background: '#1e1e1e', 
            color: '#d4d4d4', 
            padding: '16px', 
            borderRadius: '8px', 
            overflow: 'auto',
            fontSize: '13px',
            lineHeight: 1.5
          }}>
            <code>{pixelSnippet}</code>
          </pre>
        </div>
      </div>
    </div>
  );
}
